/**
 * Application bootstrap configuration for the frontend shell.
 * This file wires global providers, initialization behavior, and app-wide platform setup.
 */
import { ApplicationConfig, Injector, inject, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';

import { appRoutes } from './app.routes';
import { provideApiRepository } from './core/services/repositories/api-repo.token';
import { retailPrimePreset } from './core/ui/primeng-theme.preset';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './features/login/auth.service';
import { I18nService } from './core/i18n/i18n.service';
import { handleNavigationError } from './core/router/navigation-error.handler';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withNavigationErrorHandler(handleNavigationError)),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: retailPrimePreset
      }
    }),
    provideApiRepository(),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
        enforceLoading: !environment.production,
        useHttpBackend: true
      })
    }),
    provideAppInitializer(async () => {
      const injector = inject(Injector);
      const i18nService = inject(I18nService);

      await i18nService.init();
      const authService = injector.get(AuthService);

      if (!authService.hasStoredSession()) {
        return;
      }

      try {
        // Verify the session with the server only when local auth state exists.
        // 200 → currentUser is refreshed → canActivateAuth guard passes.
        // Any error (401, network) → clear local auth state → guard redirects to /login.
        await authService.loadProfile();
      } catch {
        authService.clearSession();
      }
    })
  ]
};
